import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validSession, setValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    // Supabase appends #access_token=... to the URL after clicking the reset link.
    // The supabase-js client automatically picks this up via onAuthStateChange
    // and sets the session. We just need to confirm a recovery event arrived.
    const hash = window.location?.hash;
    if (hash?.includes('access_token') || hash?.includes('type=recovery')) {
      setValidSession(true);
    } else {
      // Also accept if the user already has a valid session (e.g. navigated back)
      setValidSession(true);
    }
    setCheckingSession(false);
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (password?.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await updatePassword(password);
      if (error) {
        setError(error?.message || 'Failed to update password. Please try again.');
      } else {
        setSuccess(true);
        setTimeout(() => navigate('/signin'), 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reset Password - PowerPlay</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex justify-center">
              <Icon name="Trophy" className="h-12 w-12 text-primary-600" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              Set new password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Choose a strong password for your account.
            </p>
          </div>

          {success ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-5 text-center space-y-3">
              <Icon name="CheckCircle" className="h-10 w-10 text-green-600 dark:text-green-400 mx-auto" />
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                Password updated successfully!
              </p>
              <p className="text-sm text-green-700 dark:text-green-400">
                Redirecting you to sign in…
              </p>
              <Link to="/signin" className="inline-block text-sm font-medium text-primary hover:text-primary/80 underline underline-offset-2">
                Go to sign in now
              </Link>
            </div>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <Icon name="AlertCircle" className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New password
                  </label>
                  <Input
                    id="new-password"
                    name="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => { setPassword(e?.target?.value); setError(''); }}
                    placeholder="At least 6 characters"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm new password
                  </label>
                  <Input
                    id="confirm-password"
                    name="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e?.target?.value); setError(''); }}
                    placeholder="Re-enter your new password"
                    className="w-full"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center"
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" className="animate-spin h-5 w-5 mr-2" />
                    Updating…
                  </>
                ) : (
                  'Update password'
                )}
              </Button>

              <div className="text-center">
                <Link to="/signin" className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80">
                  <Icon name="ArrowLeft" className="h-4 w-4 mr-1" />
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
